import { Loader2 } from "lucide-react";

import type { Side } from "@design-lab/shared";
import type { GetCommunityImagesQuery } from "graphql-api/operations";
import { useArtworkSelection } from "../../model/useArtworkSelection";
import { MENU_IDS } from "../../model/state";
import { ImageGrid } from "./ImageGrid";
import { ImageGridItem } from "./ImageGridItem";

type CommunityImage = GetCommunityImagesQuery["communityImages"][0];

const THUMBNAIL_SIZE = 400;

function addImgixParams(url: string, width: number, height: number): string {
  if (!url) return url;

  const urlObj = new URL(url);
  urlObj.searchParams.set("w", width.toString());
  urlObj.searchParams.set("h", height.toString());

  return urlObj.toString();
}

type CommunityImageGridProps = {
  images: CommunityImage[];
  currentSide: Side;
  uploadingImageId: string | undefined;
  uploadStartTime: number | undefined;
  onImageSelect: (imageId: string, imageUrl: string, imageName: string) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  targetRef: React.RefObject<HTMLDivElement | null>;
  isDisabled?: boolean;
};

export function CommunityImageGrid({
  images,
  currentSide,
  uploadingImageId,
  uploadStartTime,
  onImageSelect,
  hasMore,
  isLoadingMore,
  targetRef
}: CommunityImageGridProps) {
  const { getActiveSelection } = useArtworkSelection();
  const activeSelection = getActiveSelection(currentSide);

  const isAnyImageUploading = !!uploadingImageId;

  return (
    <div className="space-y-8">
      <ImageGrid>
        {images.map(image => {
          const isSelected =
            activeSelection.activeSource === MENU_IDS.COMMUNITY_ART &&
            activeSelection.selectedId === parseInt(image.id, 10);
          const isUploading = uploadingImageId === image.id;
          const thumbnailUrl = addImgixParams(
            image.imageUrl as string,
            THUMBNAIL_SIZE,
            THUMBNAIL_SIZE
          );

          return (
            <ImageGridItem
              key={image.id}
              imageUrl={thumbnailUrl}
              imageName={image.name}
              isSelected={isSelected}
              isUploading={isUploading}
              startTime={uploadStartTime}
              borderColor="gray"
              disabled={isAnyImageUploading && !isUploading}
              onSelect={() => {
                if (!isAnyImageUploading) {
                  onImageSelect(image.id, image.imageUrl as string, image.name);
                }
              }}
              metadata={<p className="truncate font-medium">{image.name}</p>}
            />
          );
        })}
      </ImageGrid>

      {hasMore && (
        <div ref={targetRef} className="flex justify-center py-4">
          {isLoadingMore && (
            <div className="flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-sm text-gray-600">Loading more images...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
